const generateId = () =>
  (globalThis.crypto?.randomUUID && globalThis.crypto.randomUUID()) ||
  `mock-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const nowIso = () => new Date().toISOString();

const store = {
  transmissions: [],
  transmissionRecords: [],
  submissionEvents: [],
};

export const getStore = () => store;

export const resetCarfTransmissionMockStore = () => {
  store.transmissions = [];
  store.transmissionRecords = [];
  store.submissionEvents = [];
};

export const findTransmission = (id) =>
  store.transmissions.find((t) => t.id === id);

export const findTransmissionsByAdmin = (adminAccountId) =>
  store.transmissions.filter((t) => t.adminAccountId === adminAccountId);

export const findRecordsByTransmission = (transmissionId) =>
  store.transmissionRecords.filter((r) => r.transmissionId === transmissionId);

export const findRecordsByClient = (clientAccountId) =>
  store.transmissionRecords.filter(
    (r) => r.clientAccountId === clientAccountId,
  );

export const findChildTransmissions = (parentTransmissionId) =>
  store.transmissions.filter((t) => t.parentTransmissionId === parentTransmissionId);

export const findEventsByTransmission = (transmissionId) =>
  store.submissionEvents
    .filter((e) => e.transmissionId === transmissionId)
    .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));

// Extract the 4-digit year from a period string ("2024" or "2024-Q1")
const periodYear = (period) => String(period || '').slice(0, 4) || '0000';

// Strip hyphens from a UUID and take a suffix — used in OECD-format identifiers
const shortId = (uuid) => uuid.replace(/-/g, '').slice(0, 14);

export const createTransmission = (fields) => {
  const id = generateId();
  // MessageRefId format per OECD CARF schema: sending country code, reporting
  // year, receiving country code, then a unique suffix. Domestic collection ->
  // sending and receiving country are the same jurisdiction.
  const messageRefId =
    fields.messageRefId ||
    `${fields.jurisdiction}${periodYear(fields.period)}${fields.jurisdiction}${shortId(id)}`;
  const transmission = {
    id,
    adminAccountId: fields.adminAccountId,
    jurisdiction: fields.jurisdiction,
    period: fields.period,
    messageRefId,
    messageTypeIndic: fields.messageTypeIndic || 'CARF701',
    status: fields.status || 'draft',
    // 'xml' (default OECD XML transmission) or 'partner' (certified & flagged for
    // a designated filing partner to file). Partner rows skip the XML-specific
    // detail panels.
    submissionMode: fields.submissionMode || 'xml',
    reportingPartner: fields.reportingPartner || null,
    certifiedAt: fields.certifiedAt || null,
    certifiedBy: fields.certifiedBy || null,
    payloadHash: fields.payloadHash || null,
    payloadUri: fields.payloadUri || null,
    validationSummary: fields.validationSummary || null,
    authorityReference: fields.authorityReference || null,
    parentTransmissionId: fields.parentTransmissionId || null,
    createdBy: fields.createdBy || 'mock-user',
    submittedBy: fields.submittedBy || null,
    createdAt: fields.createdAt || nowIso(),
    submittedAt: fields.submittedAt || null,
    recordCount: fields.recordCount || 0,
    __typename: 'CarfTransmission',
  };
  store.transmissions.push(transmission);
  return transmission;
};

export const updateTransmission = (id, patch) => {
  const idx = store.transmissions.findIndex((t) => t.id === id);
  if (idx === -1) return null;
  store.transmissions[idx] = { ...store.transmissions[idx], ...patch };
  return store.transmissions[idx];
};

export const createTransmissionRecord = (fields) => {
  const id = generateId();
  // DocRefId format per OECD CARF schema: {jurisdiction}{year}{uniqueSuffix}
  let docRefId = fields.docRefId;
  if (!docRefId) {
    const parentTx = store.transmissions.find((t) => t.id === fields.transmissionId);
    const jurisdiction = parentTx?.jurisdiction || 'XX';
    const year = periodYear(parentTx?.period);
    docRefId = `${jurisdiction}${year}${shortId(id)}`;
  }
  const record = {
    id,
    transmissionId: fields.transmissionId,
    clientAccountId: fields.clientAccountId,
    clientName: fields.clientName || `Client ${fields.clientAccountId}`,
    docRefId,
    docTypeIndic: fields.docTypeIndic || 'OECD1',
    correlatedRecordId: fields.correlatedRecordId || null,
    correlatedDocRefId: fields.correlatedDocRefId || null,
    status: fields.status || 'pending',
    validationResults: fields.validationResults || [],
    __typename: 'CarfTransmissionRecord',
  };
  store.transmissionRecords.push(record);
  return record;
};

export const appendSubmissionEvent = (fields) => {
  const event = {
    id: generateId(),
    transmissionId: fields.transmissionId,
    kind: fields.kind,
    occurredAt: fields.occurredAt || nowIso(),
    actorUserId: fields.actorUserId || null,
    message: fields.message || null,
    rawPayload: fields.rawPayload || null,
    __typename: 'CarfSubmissionEvent',
  };
  store.submissionEvents.push(event);
  return event;
};

export const enrichRecordWithTransmission = (record) => {
  const transmission = findTransmission(record.transmissionId);
  return {
    ...record,
    transmission: transmission
      ? {
          id: transmission.id,
          jurisdiction: transmission.jurisdiction,
          period: transmission.period,
          status: transmission.status,
          submittedAt: transmission.submittedAt,
          __typename: 'CarfTransmission',
        }
      : null,
  };
};
